import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { categoryColors } from "@plasma/data/categories";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery as useConvexQuery } from "convex/react";
import { twJoin } from "tailwind-merge";

import { categoryCardColors } from "~/components/menus/category-card-colors";
import { AlertDialogLoading } from "~/components/shared/alert-dialog-loading";
import { toConvexTableId } from "~/lib/convex-table-id";
import { enumIncludes } from "~/lib/parse-enum-value";

const isCategoryColor = enumIncludes(categoryColors);
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { convexClient } from "~/integrations/convex/provider";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

const getPreDeleteDataRef = api.categories.getPreDeleteData;

const removeFromMenuRef = api.categories.removeFromMenu;

export const Route = createFileRoute(
  "/_authenticated/menus/$menuId/categories/$categoryId/delete"
)({
  component: DeleteCategoryModal,
});

function DeleteCategoryModal() {
  const { menuId, categoryId } = Route.useParams();
  const navigate = useNavigate();

  // Fetch category data for display
  const category = useConvexQuery(getPreDeleteDataRef, {
    id: toConvexTableId("categories", categoryId),
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: ({
      menuId: targetMenuId,
      categoryId: targetCategoryId,
    }: {
      menuId: Id<"menus">;
      categoryId: Id<"categories">;
    }) => {
      return convexClient.mutation(removeFromMenuRef, {
        menuId: targetMenuId,
        categoryId: targetCategoryId,
      });
    },
    onError: (error) => {
      logger.exception("category_deletion_failed", error);
    },
  });

  const handleClose = () => {
    void navigate({ to: `/menus/${menuId}/categories` });
  };

  const handleDelete = async () => {
    if (deleteCategoryMutation.isPending) {
      return;
    }
    await deleteCategoryMutation.mutateAsync({
      menuId: toConvexTableId("menus", menuId),
      categoryId: toConvexTableId("categories", categoryId),
    });
    // Track category deletion in PostHog
    ProductEvents.categoryDeleted(categoryId);
    handleClose();
  };

  if (!category) {
    return (
      <AlertDialogLoading
        message="Loading category details..."
        onOpenChange={handleClose}
      />
    );
  }

  return (
    <AlertDialog onOpenChange={handleClose} open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Category</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove this category from{" "}
            <b>{category.menuRelations[0]?.menu.name ?? "this menu"}</b>?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div
          className={twJoin(
            "body-sm space-y-1 rounded-2xl px-5 py-4 text-foreground",
            categoryCardColors[
              isCategoryColor(category.color) ? category.color : "white"
            ]
          )}
        >
          <p className="subtitle-sm">{category.name}</p>
          <p>{category.itemsCount} items</p>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={deleteCategoryMutation.isPending}
            onClick={handleDelete}
            variant="destructive"
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
